import Phaser from "phaser";
import { PLAYER_DISPLAY_HEIGHT_PX } from "@/character/skills/constants";
import { skillChainGcdMs } from "@/character/skills/skill-chain-gcd";
import { SceneKeys } from "@/game/data/scene-keys";
import {
  attachPlayerShadowCompanion,
  type PlayerShadowCompanion,
} from "@/game/shadow/player-shadow-companion";
import {
  attachPlayerPetCompanion,
  type PlayerPetCompanion,
} from "@/game/pet/player-pet-companion";
import { PET_DEPTH_BEHIND } from "@/game/pet/pet-constants";
import { petCombatRatings } from "@/pet/pet-combat-ratings";
import type { PetDefinition } from "@/pet/types";
import {
  fitImageContainInFootprint,
  fitImageCoverInBox,
} from "@/game/fit-image-footprint";
import { loadHtmlImageFromDataUrl } from "@/game/load-data-texture";
import {
  createInitialProgression,
  type CharacterProgression,
} from "@/game/model/character-progression";
import {
  attackGapMs,
  mergeStatBonuses,
  moveSpeedMultiplier,
  playerCombatMaxHp,
  playerRatings,
  type CombatRatings,
  type StatAllocation,
} from "@/game/model/character-stats";
import { resolveHit } from "@/game/model/combat-resolve";
import {
  combatManaRegenPerSec,
  playerCombatMaxMana,
  SKILL_MANA_COST,
} from "@/game/model/combat-mana";
import {
  applyHpRegenTick,
  combatRatingsHpRegenPerSec,
} from "@/game/model/player-hp-regen";
import {
  CRIT_TINT_GOLD,
  FloatingCombatTextPool,
} from "@/game/ui/floating-combat-text";
import { SceneSkillFx } from "@/game/skills/scene-skill-fx";
import {
  HomeEvents,
  type HomeSkillRuntimePayload,
  type HomeUseSkillPayload,
} from "@/home/home-events";
import {
  TowerEvents,
  type TowerPlayerManaChangedPayload,
  type TowerSkillUseAcceptedPayload,
  type TowerSkillUseRejectedPayload,
} from "@/tower/tower-events";
import type { HomeSkillRuntimeSkill } from "@/home/home-events";
import { normalizeImageDataUrl } from "@/home/image-data-url";
import type { HomePartKind } from "@/home/part-kind";
import { themeById, type HomeThemeId } from "@/home/themes";
import type { TowerEnemySpawn, TowerFloorBattlePlan } from "@/tower/tower-floor-runtime";
import { towerFloorDefinition } from "@/tower/tower-floors";
import {
  fitTowerMonsterSprite,
  registerTowerMonsterSheet,
  towerMonsterIdleAnimKey,
  towerSpawnHasSheet,
} from "@/tower/tower-monster-sheet";
import { towerSpawnDelayMs } from "@/tower/tower-spawn-rate";
import { attachPlayerRankBadges, RankBadgeOverhead } from "@/rank/rank-badge-overhead";

const ENEMY_DISPLAY_H = 56;
const GRID_COLS = 22;
const GRID_ROWS = 14;
const CELL_SIZE = 48;
const WORLD_W = GRID_COLS * CELL_SIZE;
const WORLD_H = GRID_ROWS * CELL_SIZE;
const PLAY_X = CELL_SIZE;
const PLAY_Y = CELL_SIZE * 2;
const PLAY_W = WORLD_W - CELL_SIZE * 2;
const PLAY_H = WORLD_H - CELL_SIZE * 3;
const PLAYER_ATTACK_RANGE = 78;
const PLAYER_SKILL_RANGE = 112;
const ENEMY_ATTACK_RANGE = 62;
const PLAYER_BASE_SPEED = 200;
const ENEMY_MOVE_SPEED = 105;
const WANDER_CHANGE_MS = 2400;

type ActiveEnemy = {
  spawn: TowerEnemySpawn;
  hp: number;
  maxHp: number;
  ratings: CombatRatings;
  gfx: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle | Phaser.GameObjects.Sprite;
  animated: boolean;
  attackCooldownMs: number;
  wanderVx: number;
  wanderVy: number;
  wanderUntil: number;
};

function playerTextureKey(scene: Phaser.Scene): { key: string; frame?: number } {
  if (scene.textures.exists("player-idle")) return { key: "player-idle" };
  if (scene.textures.exists("player-spritesheet")) return { key: "player-spritesheet", frame: 0 };
  if (scene.textures.exists("player-placeholder")) return { key: "player-placeholder" };
  return { key: "__MISSING" };
}

function applyPlayerScale(sprite: Phaser.GameObjects.Sprite): void {
  const h = sprite.height;
  if (h <= 0) return;
  sprite.setScale(PLAYER_DISPLAY_HEIGHT_PX / h);
}

function applyEnemyScale(img: Phaser.GameObjects.Image): void {
  const h = img.height;
  if (h <= 0) return;
  img.setScale(ENEMY_DISPLAY_H / h);
}

function gridToWorld(col: number, row: number): { x: number; y: number } {
  return {
    x: col * CELL_SIZE + CELL_SIZE / 2,
    y: row * CELL_SIZE + CELL_SIZE * 0.92,
  };
}

function scenePieceFillsArena(piece: {
  kind: HomePartKind;
  cellsW: number;
  cellsH: number;
}): boolean {
  if (piece.kind !== "backdrop" && piece.kind !== "floor") return false;
  const arenaW = GRID_COLS - 2;
  const arenaH = GRID_ROWS - 3;
  return piece.cellsW >= arenaW && piece.cellsH >= arenaH;
}

function clampToArena(x: number, y: number): { x: number; y: number } {
  const pad = CELL_SIZE * 0.35;
  return {
    x: Phaser.Math.Clamp(x, pad, WORLD_W - pad),
    y: Phaser.Math.Clamp(y, pad + CELL_SIZE, WORLD_H - pad),
  };
}

export class TowerScene extends Phaser.Scene {
  private floor = 1;
  private plan!: TowerFloorBattlePlan;
  private pendingSpawns: TowerEnemySpawn[] = [];
  private enemies: ActiveEnemy[] = [];
  private activeTarget: ActiveEnemy | null = null;
  private progression: CharacterProgression = createInitialProgression();
  private playerMaxHp = 100;
  private playerHp = 100;
  private hpRegenCarry = 0;
  private playerMaxMana = 100;
  private playerMana = 100;
  private manaRegenCarry = 0;
  private player!: Phaser.GameObjects.Sprite;
  private ambienceGfx!: Phaser.GameObjects.Graphics;
  private gridGfx!: Phaser.GameObjects.Graphics;
  private floorGfx!: Phaser.GameObjects.Graphics;
  private walkZone!: Phaser.GameObjects.Zone;
  private combatFloaters: FloatingCombatTextPool | null = null;
  private spawnTimer: Phaser.Time.TimerEvent | null = null;
  private keys!: {
    up: Phaser.Input.Keyboard.Key;
    down: Phaser.Input.Keyboard.Key;
    left: Phaser.Input.Keyboard.Key;
    right: Phaser.Input.Keyboard.Key;
    w: Phaser.Input.Keyboard.Key;
    a: Phaser.Input.Keyboard.Key;
    s: Phaser.Input.Keyboard.Key;
    d: Phaser.Input.Keyboard.Key;
  };
  private moveTarget: { x: number; y: number } | null = null;
  private playerAttackCooldownMs = 0;
  private battleActive = false;
  private ended = false;
  private playerDowned = false;
  private deathFx: Phaser.GameObjects.Graphics | null = null;
  private totalXpEarned = 0;
  private enemiesKilled = 0;
  private attackingAnim = false;
  private autoChaseEnabled = false;
  private skillFx!: SceneSkillFx;
  private rankBadges: RankBadgeOverhead | null = null;
  private playerShadow: PlayerShadowCompanion | null = null;
  private playerPet: PlayerPetCompanion | null = null;
  private equippedPetDef: PetDefinition | null = null;

  constructor() {
    super(SceneKeys.Tower);
  }

  create(): void {
    this.floor = (this.registry.get("towerFloor") as number | undefined) ?? 1;
    const prog = this.registry.get("characterProgression") as CharacterProgression | undefined;
    if (prog) this.progression = prog;

    const plan = this.registry.get("towerBattlePlan") as TowerFloorBattlePlan | undefined;
    if (plan && plan.floor === this.floor) {
      this.plan = plan;
    } else {
      const def = towerFloorDefinition(this.floor);
      this.plan = {
        floor: this.floor,
        name: def.name,
        totalXpReward: def.xpReward,
        customConfig: false,
        powerMultiplier: 1,
        scenePieces: [],
        spawns: [
          {
            instanceKey: `proc-${this.floor}`,
            monsterId: "",
            name: def.name,
            imageDataUrl: "",
            cellsH: 1,
            maxHp: def.maxHp,
            xpReward: def.xpReward,
            ratings: def.ratings,
          },
        ],
      };
    }

    this.pendingSpawns = [...this.plan.spawns];

    this.ambienceGfx = this.add.graphics().setDepth(-35);
    this.gridGfx = this.add.graphics().setDepth(0);
    this.floorGfx = this.add.graphics().setDepth(-1);
    void this.drawSceneAmbience();
    this.drawArena();
    void this.drawScenePieces();

    const start = gridToWorld(Math.floor(GRID_COLS * 0.28), Math.floor(GRID_ROWS * 0.55));
    const texture = playerTextureKey(this);
    this.player = this.add.sprite(start.x, start.y, texture.key, texture.frame);
    this.player.setOrigin(0.5, 1);
    applyPlayerScale(this.player);
    if (this.anims.exists("player-idle")) {
      this.player.play("player-idle");
    }
    this.playerShadow = attachPlayerShadowCompanion(this, this.player);

    const equippedPet = this.registry.get("equippedPet") as PetDefinition | undefined;
    const petSkillRuntime = this.registry.get("petSkillRuntime") as
      | HomeSkillRuntimePayload
      | undefined;
    if (equippedPet) {
      this.equippedPetDef = equippedPet;
      this.playerPet = attachPlayerPetCompanion(this, this.player, {
        progression: this.progression,
        pet: equippedPet,
        skillRuntime: petSkillRuntime,
        battleActive: () => this.battleActive && !this.ended && !this.playerDowned,
        resolveDisplayDepth: (pet, player) =>
          Math.min(player.depth - PET_DEPTH_BEHIND, pet.y - 0.05),
      });
      this.playerPet?.setTargetFinder((range) => {
        const enemy = this.nearestEnemy(range);
        if (!enemy) return null;
        return {
          gfx: enemy.gfx,
          ratings: enemy.ratings,
          hp: enemy.hp,
        };
      });
      this.playerPet?.setStrikeResolver((target, mult = 1) => {
        const match = this.enemies.find((e) => e.gfx === target.gfx);
        if (match) this.resolvePetStrike(match, mult);
      });
    }

    void attachPlayerRankBadges(this, this.player)
      .then((o) => {
        this.rankBadges = o;
      })
      .catch(() => {
        this.rankBadges = null;
      });

    this.walkZone = this.add.zone(0, 0, WORLD_W, WORLD_H).setInteractive();
    this.walkZone.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (this.ended) return;
      this.moveTarget = { x: pointer.worldX, y: pointer.worldY };
    });

    this.keys = this.input.keyboard!.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.UP,
      down: Phaser.Input.Keyboard.KeyCodes.DOWN,
      left: Phaser.Input.Keyboard.KeyCodes.LEFT,
      right: Phaser.Input.Keyboard.KeyCodes.RIGHT,
      w: "W",
      a: "A",
      s: "S",
      d: "D",
    }) as TowerScene["keys"];

    this.combatFloaters = new FloatingCombatTextPool(this);
    this.skillFx = new SceneSkillFx(this, () => this.player);
    const skillRuntime = this.registry.get("homeSkillRuntime") as HomeSkillRuntimePayload | undefined;
    if (skillRuntime) this.skillFx.setRuntime(skillRuntime);
    this.initPlayerHp();
    this.initPlayerMana();
    this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        this.tickPlayerHpRegen();
        this.tickPlayerManaRegen();
      },
    });
    window.addEventListener(HomeEvents.UseSkill, this.onUseSkill);
    window.addEventListener(HomeEvents.SkillRuntimeChanged, this.onSkillRuntimeChanged);
    window.addEventListener(TowerEvents.AutoChaseChanged, this.onAutoChaseChanged);
    window.addEventListener(TowerEvents.ReviveAccepted, this.onReviveAccepted);
    window.addEventListener(TowerEvents.BattleGiveUp, this.onBattleGiveUp);
    window.addEventListener(TowerEvents.HealthPotionHeal, this.onHealthPotionHeal);

    this.setupCamera();
    this.emitFocusTargetChanged(0);

    this.battleActive = true;
    void this.spawnNextEnemy();

    this.scale.on("resize", this.onResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off("resize", this.onResize, this);
      window.removeEventListener(HomeEvents.UseSkill, this.onUseSkill);
      window.removeEventListener(HomeEvents.SkillRuntimeChanged, this.onSkillRuntimeChanged);
      window.removeEventListener(TowerEvents.AutoChaseChanged, this.onAutoChaseChanged);
      window.removeEventListener(TowerEvents.ReviveAccepted, this.onReviveAccepted);
      window.removeEventListener(TowerEvents.BattleGiveUp, this.onBattleGiveUp);
      window.removeEventListener(TowerEvents.HealthPotionHeal, this.onHealthPotionHeal);
      this.emitFocusTargetChanged(0);
      this.clearDeathFx();
      this.stopCombat();
      this.skillFx.destroy();
      this.combatFloaters?.destroy();
      this.combatFloaters = null;
      this.rankBadges?.destroy();
      this.rankBadges = null;
    });
  }

  private onSkillRuntimeChanged = (event: Event): void => {
    this.skillFx.setRuntime((event as CustomEvent<HomeSkillRuntimePayload>).detail);
  };

  private onAutoChaseChanged = (event: Event): void => {
    const { enabled } = (event as CustomEvent<{ enabled: boolean }>).detail;
    this.autoChaseEnabled = enabled;
    if (enabled) this.moveTarget = null;
  };

  private rejectSkillUse(slotIndex: number): void {
    window.dispatchEvent(
      new CustomEvent(TowerEvents.SkillUseRejected, {
        detail: { slotIndex } satisfies TowerSkillUseRejectedPayload,
      }),
    );
  }

  private acceptSkillUse(slotIndex: number): void {
    const ratings = this.playerCombatRatings();
    window.dispatchEvent(
      new CustomEvent(TowerEvents.SkillUseAccepted, {
        detail: {
          slotIndex,
          chainGcdMs: skillChainGcdMs(ratings.level, ratings.attackSpeed),
        } satisfies TowerSkillUseAcceptedPayload,
      }),
    );
  }

  private emitPlayerManaChanged(): void {
    window.dispatchEvent(
      new CustomEvent(TowerEvents.PlayerManaChanged, {
        detail: {
          current: this.playerMana,
          max: this.playerMaxMana,
        } satisfies TowerPlayerManaChangedPayload,
      }),
    );
  }

  private spendPlayerMana(amount: number): boolean {
    if (this.playerMana < amount) return false;
    this.playerMana -= amount;
    this.emitPlayerManaChanged();
    return true;
  }

  private canCastSkill(skill: HomeSkillRuntimeSkill): boolean {
    if (skill.kind === "buff") {
      return !this.skillFx.hasActiveBuff();
    }
    if (skill.attackPattern === "aoe") {
      return this.enemiesInAoeAroundPlayer(skill.aoeRangeMultiplier ?? 1).length > 0;
    }
    return this.nearestEnemy(PLAYER_SKILL_RANGE) !== null;
  }

  private onUseSkill = (event: Event): void => {
    const { skillId, slotIndex } = (event as CustomEvent<HomeUseSkillPayload>).detail;
    if (!this.battleActive || this.ended || this.playerDowned) {
      this.rejectSkillUse(slotIndex);
      return;
    }
    const skill = this.skillFx.getSkill(skillId);
    if (!skill || !this.canCastSkill(skill)) {
      this.rejectSkillUse(slotIndex);
      return;
    }
    if (!this.spendPlayerMana(SKILL_MANA_COST)) {
      this.rejectSkillUse(slotIndex);
      return;
    }

    if (skill.kind === "buff") {
      this.attackingAnim = false;
      this.skillFx.playSkill(skillId, () => {});
      this.acceptSkillUse(slotIndex);
      return;
    }

    this.skillFx.playSkill(skillId, (hitSkill, damageMultiplier) => {
      if (hitSkill.attackPattern === "aoe") {
        const hits = this.enemiesInAoeAroundPlayer(hitSkill.aoeRangeMultiplier ?? 1);
        for (const enemy of hits) {
          this.resolveStrike(enemy, damageMultiplier);
        }
        const focus = hits[0];
        if (focus) {
          this.activeTarget = focus;
          this.facePlayerToward(focus.gfx.x);
          this.emitFocusTargetChanged(0);
        }
        return;
      }
      const target = this.nearestEnemy(PLAYER_SKILL_RANGE);
      if (!target) return;
      this.activeTarget = target;
      this.emitFocusTargetChanged(0);
      this.resolveStrike(target, damageMultiplier);
      this.facePlayerToward(target.gfx.x);
    });

    if (this.anims.exists("player-attack")) {
      this.attackingAnim = true;
      this.player.once("animationcomplete", (anim: Phaser.Animations.Animation) => {
        if (anim.key !== "player-attack") return;
        this.attackingAnim = false;
        if (this.anims.exists("player-idle")) this.player.play("player-idle", true);
      });
    }
    this.acceptSkillUse(slotIndex);
  };

  update(_time: number, delta: number): void {
    if (!this.battleActive || this.ended || this.playerDowned) return;
    this.skillFx.tickFollowPlayer();
    this.tickPlayerMovement(delta);
    this.tickEnemies(delta);
    this.tickPlayerAutoAttack(delta);
    this.refreshDepths();
    this.syncActiveTarget();
    this.rankBadges?.sync();
    this.playerShadow?.syncFromPlayer(this.player);
    this.playerPet?.tick(delta, this.player);
  }

  private async ensureAmbienceTexture(key: string, dataUrl: string): Promise<boolean> {
    if (!dataUrl.startsWith("data:")) return false;
    if (this.textures.exists(key)) return true;
    try {
      const img = await loadHtmlImageFromDataUrl(dataUrl);
      if (this.textures.exists(key)) this.textures.remove(key);
      this.textures.addImage(key, img);
      return true;
    } catch {
      return false;
    }
  }

  private paintThemeAmbience(themeId: HomeThemeId, shellFramed: boolean): void {
    const theme = themeById(themeId);
    const fillX = shellFramed ? PLAY_X : 0;
    const fillY = shellFramed ? PLAY_Y : 0;
    const fillW = shellFramed ? PLAY_W : WORLD_W;
    const fillH = shellFramed ? PLAY_H : WORLD_H;
    this.ambienceGfx.clear();
    this.ambienceGfx.fillGradientStyle(
      theme.gradientTop,
      theme.gradientTop,
      theme.gradientBottom,
      theme.gradientBottom,
      1,
    );
    this.ambienceGfx.fillRect(fillX, fillY, fillW, fillH);
    const midY = fillY + fillH * 0.35;
    this.ambienceGfx.fillStyle(theme.gradientMid, theme.midAlpha);
    this.ambienceGfx.fillRect(fillX, midY, fillW, fillH * 0.4);
    if (shellFramed) {
      this.ambienceGfx.fillStyle(0x0c121c, theme.dimAlpha);
      this.ambienceGfx.fillRect(fillX, fillY, fillW, fillH);
    }
  }

  private async drawSceneAmbience(): Promise<void> {
    const amb = this.plan.sceneAmbience;
    if (!amb) return;

    const hasShell = Boolean(amb.shellImageDataUrl);
    const playCx = PLAY_X + PLAY_W / 2;
    const playCy = PLAY_Y + PLAY_H / 2;

    if (amb.shellImageDataUrl) {
      const key = "tower-ambience-shell";
      if (await this.ensureAmbienceTexture(key, amb.shellImageDataUrl)) {
        const img = this.add.image(WORLD_W / 2, WORLD_H / 2, key).setDepth(-100);
        fitImageCoverInBox(img, WORLD_W, WORLD_H);
      }
    }

    const customBackdrop = amb.backdropCustomUrl
      ? normalizeImageDataUrl(amb.backdropCustomUrl)
      : null;
    const backdropUrl = amb.backdropImageDataUrl ?? customBackdrop;

    if (backdropUrl?.startsWith("data:")) {
      const key = "tower-ambience-backdrop";
      if (await this.ensureAmbienceTexture(key, backdropUrl)) {
        const cx = hasShell ? playCx : WORLD_W / 2;
        const cy = hasShell ? playCy : WORLD_H / 2;
        const w = hasShell ? PLAY_W : WORLD_W;
        const h = hasShell ? PLAY_H : WORLD_H;
        const img = this.add.image(cx, cy, key).setDepth(-25);
        fitImageCoverInBox(img, w, h);
      }
    } else {
      this.paintThemeAmbience(amb.themeId, hasShell);
    }

    if (amb.floorSkinImageDataUrl) {
      const key = "tower-ambience-floor-skin";
      if (await this.ensureAmbienceTexture(key, amb.floorSkinImageDataUrl)) {
        const img = this.add.image(playCx, playCy, key).setDepth(-18);
        fitImageCoverInBox(img, PLAY_W, PLAY_H);
      }
    }
  }

  private async drawScenePieces(): Promise<void> {
    const pieces = this.plan.scenePieces ?? [];
    for (const piece of pieces) {
      if (!piece.imageDataUrl.startsWith("data:")) continue;
      const texKey = `tower-scene-${piece.instanceKey}`;
      if (!this.textures.exists(texKey)) {
        try {
          const img = await loadHtmlImageFromDataUrl(piece.imageDataUrl);
          if (this.textures.exists(texKey)) this.textures.remove(texKey);
          this.textures.addImage(texKey, img);
        } catch {
          continue;
        }
      }
      const kind = piece.kind;
      const arenaFill = scenePieceFillsArena(piece);
      if (kind === "backdrop" && arenaFill) {
        const img = this.add.image(WORLD_W / 2, WORLD_H / 2, texKey);
        fitImageCoverInBox(img, WORLD_W, WORLD_H);
        img.setDepth(-20);
        continue;
      }
      if (kind === "floor" && arenaFill) {
        const img = this.add.image(WORLD_W / 2, WORLD_H / 2, texKey);
        fitImageCoverInBox(img, WORLD_W - CELL_SIZE * 2, WORLD_H - CELL_SIZE * 3);
        img.setDepth(-15);
        continue;
      }
      const w = piece.cellsW * CELL_SIZE;
      const h = piece.cellsH * CELL_SIZE;
      const x = piece.gridX * CELL_SIZE + w / 2;
      const y = (piece.gridY + piece.cellsH) * CELL_SIZE;
      const img = this.add.image(x, y, texKey);
      fitImageContainInFootprint(img, w, h);
      img.setDepth(piece.depth);
    }
  }

  private drawArena(): void {
    const amb = this.plan.sceneAmbience;
    const hasShell = Boolean(amb?.shellImageDataUrl);
    const hasFloorSkin = Boolean(amb?.floorSkinImageDataUrl);
    const hasBackdrop =
      Boolean(amb?.backdropImageDataUrl) || Boolean(amb?.backdropCustomUrl);

    this.floorGfx.clear();
    if (!hasShell) {
      this.floorGfx.fillStyle(0x121820, 1);
      this.floorGfx.fillRect(0, 0, WORLD_W, WORLD_H);
    }
    if (!hasFloorSkin && !hasBackdrop && !hasShell) {
      this.floorGfx.fillStyle(0x1a2430, 1);
      this.floorGfx.fillRect(PLAY_X, PLAY_Y, PLAY_W, PLAY_H);
    } else if (!hasFloorSkin && hasShell) {
      this.floorGfx.fillStyle(0x0c121c, 0.15);
      this.floorGfx.fillRect(PLAY_X, PLAY_Y, PLAY_W, PLAY_H);
    }

    this.gridGfx.clear();
    this.gridGfx.lineStyle(1, 0x2d3a4a, 0.45);
    for (let c = 0; c <= GRID_COLS; c += 1) {
      const x = c * CELL_SIZE;
      this.gridGfx.lineBetween(x, CELL_SIZE * 2, x, WORLD_H - CELL_SIZE);
    }
    for (let r = 2; r <= GRID_ROWS - 1; r += 1) {
      const y = r * CELL_SIZE;
      this.gridGfx.lineBetween(CELL_SIZE, y, WORLD_W - CELL_SIZE, y);
    }
  }

  private setupCamera(): void {
    this.cameras.main.setBounds(0, 0, WORLD_W, WORLD_H);
    this.cameras.main.startFollow(this.player, true, 0.14, 0.14);
    this.cameras.main.setZoom(1);
  }

  private initPlayerHp(): void {
    const r = this.playerCombatRatings();
    const nextMax = playerCombatMaxHp(r);
    const hadState = this.playerMaxHp > 0 && this.playerHp > 0;
    this.playerMaxHp = nextMax;
    this.playerHp = hadState ? Math.min(this.playerHp, nextMax) : nextMax;
    this.emitPlayerHpChanged(0);
  }

  private initPlayerMana(): void {
    const r = this.playerCombatRatings();
    const nextMax = playerCombatMaxMana(r);
    const stored = this.registry.get("combatManaCurrent") as number | null | undefined;
    this.playerMaxMana = nextMax;
    if (typeof stored === "number" && Number.isFinite(stored)) {
      this.playerMana = Math.max(0, Math.min(nextMax, Math.floor(stored)));
    } else {
      this.playerMana = nextMax;
    }
    this.emitPlayerManaChanged();
  }

  private tickPlayerHpRegen = (): void => {
    if (!this.battleActive || this.playerDowned) return;
    const regen = combatRatingsHpRegenPerSec(this.playerCombatRatings());
    const result = applyHpRegenTick(
      this.playerHp,
      this.playerMaxHp,
      regen,
      this.hpRegenCarry,
    );
    this.hpRegenCarry = result.carry;
    if (result.healed <= 0) return;
    this.playerHp = result.current;
    this.emitPlayerHpChanged(0);
  };

  private tickPlayerManaRegen = (): void => {
    if (this.playerDowned) return;
    const result = applyHpRegenTick(
      this.playerMana,
      this.playerMaxMana,
      combatManaRegenPerSec(),
      this.manaRegenCarry,
    );
    this.manaRegenCarry = result.carry;
    if (result.healed <= 0) return;
    this.playerMana = result.current;
    this.emitPlayerManaChanged();
  };

  private emitPlayerHpChanged(damageTaken: number): void {
    window.dispatchEvent(
      new CustomEvent(TowerEvents.PlayerHpChanged, {
        detail: {
          current: this.playerHp,
          max: this.playerMaxHp,
          damageTaken,
        },
      }),
    );
  }

  private focusTargetPortraitUrl(spawn: TowerEnemySpawn): string {
    if (spawn.imageDataUrl.startsWith("data:")) return spawn.imageDataUrl;
    return "";
  }

  private emitFocusTargetChanged(damageTaken: number): void {
    const target = this.activeTarget;
    if (!target || target.hp <= 0) {
      window.dispatchEvent(
        new CustomEvent(TowerEvents.FocusTargetChanged, {
          detail: { target: null, damageTaken: 0 },
        }),
      );
      return;
    }
    window.dispatchEvent(
      new CustomEvent(TowerEvents.FocusTargetChanged, {
        detail: {
          target: {
            name: target.spawn.name,
            imageDataUrl: this.focusTargetPortraitUrl(target.spawn),
            currentHp: target.hp,
            maxHp: target.maxHp,
          },
          damageTaken,
        },
      }),
    );
  }

  private onResize = (): void => {
    /* HUD: focus card is fixed below home-hud-top */
  };

  private equipmentBonus(): Partial<StatAllocation> | undefined {
    return this.registry.get("equipmentStatBonus") as Partial<StatAllocation> | undefined;
  }

  private playerCombatRatings(): CombatRatings {
    const bonus = mergeStatBonuses(this.equipmentBonus(), this.skillFx?.buffStatBonus());
    return playerRatings(this.progression, bonus);
  }

  /** Sprite art faces left by default — flip when moving/facing right. */
  private facePlayerToward(worldX: number): void {
    this.player.setFlipX(worldX > this.player.x);
  }

  private playerSpeedPxPerSec(): number {
    const mult = moveSpeedMultiplier(this.playerCombatRatings().moveSpeed);
    return PLAYER_BASE_SPEED * mult;
  }

  private stopCombat(): void {
    this.battleActive = false;
    this.spawnTimer?.remove(false);
    this.spawnTimer = null;
    this.emitFocusTargetChanged(0);
  }

  private spawnDelayMs(afterSpawn: TowerEnemySpawn): number {
    return towerSpawnDelayMs(afterSpawn.spawnPerMinute);
  }

  private scheduleNextSpawn(afterSpawn: TowerEnemySpawn): void {
    if (!this.battleActive || this.ended) return;
    if (this.pendingSpawns.length === 0) return;
    this.spawnTimer?.remove(false);
    this.spawnTimer = this.time.delayedCall(this.spawnDelayMs(afterSpawn), () => {
      void this.spawnNextEnemy();
    });
  }

  private randomSpawnWorld(): { x: number; y: number } {
    const side = Phaser.Math.Between(0, 3);
    const col = Phaser.Math.Between(1, GRID_COLS - 2);
    const row = Phaser.Math.Between(3, GRID_ROWS - 2);
    if (side === 0) return gridToWorld(1, row);
    if (side === 1) return gridToWorld(GRID_COLS - 2, row);
    if (side === 2) return gridToWorld(col, 3);
    return gridToWorld(col, GRID_ROWS - 2);
  }

  private async ensureEnemyTexture(spawn: TowerEnemySpawn): Promise<string | null> {
    const key = `tower-enemy-${spawn.instanceKey}`;
    if (this.textures.exists(key)) return key;
    if (!spawn.imageDataUrl?.startsWith("data:")) return null;
    try {
      const img = await loadHtmlImageFromDataUrl(spawn.imageDataUrl);
      if (this.textures.exists(key)) this.textures.remove(key);
      this.textures.addImage(key, img);
      return key;
    } catch {
      return null;
    }
  }

  private pickWanderVelocity(): { vx: number; vy: number; until: number } {
    const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
    const speed = ENEMY_MOVE_SPEED * Phaser.Math.FloatBetween(0.35, 0.7);
    return {
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      until: this.time.now + WANDER_CHANGE_MS + Phaser.Math.Between(0, 800),
    };
  }

  private async spawnNextEnemy(): Promise<void> {
    if (!this.battleActive || this.ended) return;
    const spawn = this.pendingSpawns.shift();
    if (!spawn) {
      this.checkVictory();
      return;
    }

    const pos = this.randomSpawnWorld();
    let gfx: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle | Phaser.GameObjects.Sprite;
    let animated = false;
    if (towerSpawnHasSheet(spawn) && spawn.grid && spawn.spritesheetDataUrl) {
      const sheetKey = await registerTowerMonsterSheet(
        this,
        spawn.instanceKey,
        spawn.spritesheetDataUrl,
        spawn.grid,
      );
      const sprite = this.add.sprite(pos.x, pos.y, sheetKey, 0);
      fitTowerMonsterSprite(sprite, spawn.grid, spawn.cellsH);
      const idleKey = towerMonsterIdleAnimKey(spawn.instanceKey);
      if (this.anims.exists(idleKey)) {
        sprite.play(idleKey);
      }
      gfx = sprite;
      animated = true;
    } else {
      const texKey = await this.ensureEnemyTexture(spawn);
      if (texKey) {
        const img = this.add.image(pos.x, pos.y, texKey);
        img.setOrigin(0.5, 1);
        applyEnemyScale(img);
        gfx = img;
      } else {
        gfx = this.add.rectangle(pos.x, pos.y - 28, 40, 52, 0xc45c4c).setStrokeStyle(2, 0xff8888);
      }
    }
    gfx.setAlpha(0);
    gfx.setDepth(pos.y);
    this.tweens.add({ targets: gfx, alpha: 1, duration: 280 });

    const wander = this.pickWanderVelocity();
    const enemy: ActiveEnemy = {
      spawn,
      hp: spawn.maxHp,
      maxHp: spawn.maxHp,
      ratings: spawn.ratings,
      gfx,
      animated,
      attackCooldownMs: 0,
      wanderVx: wander.vx,
      wanderVy: wander.vy,
      wanderUntil: wander.until,
    };
    this.enemies.push(enemy);

    if (this.pendingSpawns.length > 0) {
      this.scheduleNextSpawn(spawn);
    } else {
      this.checkVictory();
    }
  }

  private tickPlayerMovement(delta: number): void {
    let dx = 0;
    let dy = 0;
    if (this.keys.left.isDown || this.keys.a.isDown) dx -= 1;
    if (this.keys.right.isDown || this.keys.d.isDown) dx += 1;
    if (this.keys.up.isDown || this.keys.w.isDown) dy -= 1;
    if (this.keys.down.isDown || this.keys.s.isDown) dy += 1;

    const speed = this.playerSpeedPxPerSec() * (delta / 1000);

    if (dx !== 0 || dy !== 0) {
      this.moveTarget = null;
      const len = Math.hypot(dx, dy) || 1;
      dx = (dx / len) * speed;
      dy = (dy / len) * speed;
      const next = clampToArena(this.player.x + dx, this.player.y + dy);
      this.player.setPosition(next.x, next.y);
      this.facePlayerToward(this.player.x + dx);
      if (!this.attackingAnim && this.anims.exists("player-run")) {
        this.player.play("player-run", true);
      }
      return;
    }

    if (this.autoChaseEnabled) {
      const chase = this.nearestEnemy();
      if (chase) {
        const tx = chase.gfx.x;
        const ty = chase.gfx.y;
        const dist = Phaser.Math.Distance.Between(this.player.x, this.player.y, tx, ty);
        const stopDist = PLAYER_ATTACK_RANGE * 0.82;
        if (dist > stopDist) {
          const step = Math.min(speed, dist - stopDist);
          const nx = this.player.x + ((tx - this.player.x) / dist) * step;
          const ny = this.player.y + ((ty - this.player.y) / dist) * step;
          const next = clampToArena(nx, ny);
          this.player.setPosition(next.x, next.y);
          this.facePlayerToward(tx);
          if (!this.attackingAnim && this.anims.exists("player-run")) {
            this.player.play("player-run", true);
          }
          return;
        }
      }
    }

    if (this.moveTarget) {
      const dist = Phaser.Math.Distance.Between(
        this.player.x,
        this.player.y,
        this.moveTarget.x,
        this.moveTarget.y,
      );
      if (dist < 6) {
        this.moveTarget = null;
        if (!this.attackingAnim && this.anims.exists("player-idle")) {
          this.player.play("player-idle", true);
        }
        return;
      }
      const step = Math.min(speed, dist);
      const nx = this.player.x + ((this.moveTarget.x - this.player.x) / dist) * step;
      const ny = this.player.y + ((this.moveTarget.y - this.player.y) / dist) * step;
      const next = clampToArena(nx, ny);
      this.player.setPosition(next.x, next.y);
      this.facePlayerToward(this.moveTarget.x);
      if (!this.attackingAnim && this.anims.exists("player-run")) {
        this.player.play("player-run", true);
      }
      return;
    }

    if (!this.attackingAnim && this.anims.exists("player-idle")) {
      this.player.play("player-idle", true);
    }
  }

  private tickEnemies(delta: number): void {
    const px = this.player.x;
    const py = this.player.y - 24;
    const dt = delta / 1000;

    for (const enemy of this.enemies) {
      if (enemy.hp <= 0) continue;
      const ex = enemy.gfx.x;
      const ey = enemy.gfx.y - (enemy.gfx instanceof Phaser.GameObjects.Rectangle ? 28 : 24);
      const dist = Phaser.Math.Distance.Between(px, py, ex, ey);
      const gap = attackGapMs(enemy.ratings.level, enemy.ratings.attackSpeed) + 350;
      enemy.attackCooldownMs = Math.max(0, enemy.attackCooldownMs - delta);

      let vx = 0;
      let vy = 0;
      const aggro = 520;
      if (dist < aggro) {
        const angle = Math.atan2(py - ey, px - ex);
        const chase = ENEMY_MOVE_SPEED * (dist > ENEMY_ATTACK_RANGE * 0.85 ? 1 : 0.35);
        vx = Math.cos(angle) * chase;
        vy = Math.sin(angle) * chase;
      } else {
        if (this.time.now >= enemy.wanderUntil) {
          const w = this.pickWanderVelocity();
          enemy.wanderVx = w.vx;
          enemy.wanderVy = w.vy;
          enemy.wanderUntil = w.until;
        }
        vx = enemy.wanderVx;
        vy = enemy.wanderVy;
      }

      const next = clampToArena(enemy.gfx.x + vx * dt, enemy.gfx.y + vy * dt);
      enemy.gfx.setPosition(next.x, next.y);
      this.syncEnemyMotionAnim(enemy, vx, vy, px);

      if (dist <= ENEMY_ATTACK_RANGE && enemy.attackCooldownMs <= 0) {
        enemy.attackCooldownMs = gap;
        this.enemyStrikePlayer(enemy);
      }
    }
  }

  private syncEnemyMotionAnim(
    enemy: ActiveEnemy,
    vx: number,
    vy: number,
    playerX: number,
  ): void {
    if (!enemy.animated || !(enemy.gfx instanceof Phaser.GameObjects.Sprite)) return;
    const idleKey = towerMonsterIdleAnimKey(enemy.spawn.instanceKey);
    if (this.anims.exists(idleKey) && enemy.gfx.anims.currentAnim?.key !== idleKey) {
      enemy.gfx.play(idleKey, true);
    }
    if (Math.abs(vx) + Math.abs(vy) > 4) {
      enemy.gfx.setFlipX(playerX < enemy.gfx.x);
    }
  }

  private enemyStrikePlayer(enemy: ActiveEnemy): void {
    const defender = this.playerCombatRatings();
    const result = resolveHit(enemy.ratings, defender, 1);
    const px = this.player.x;
    const py = this.player.y - 40;
    if (this.combatFloaters) {
      if (result.kind === "miss") {
        this.combatFloaters.spawn(px, py, "miss");
      } else {
        this.combatFloaters.spawn(px, py, result.crit ? "crit" : "hit", result.damage);
      }
    }
    if (result.kind === "hit") {
      this.playerHp = Math.max(0, this.playerHp - result.damage);
      this.emitPlayerHpChanged(result.damage);
      if (this.playerHp <= 0) {
        this.onPlayerDefeated();
      }
    }
  }

  private enemiesInAoeAroundPlayer(rangeMultiplier = 1): ActiveEnemy[] {
    const px = this.player.x;
    const py = this.player.y - 24;
    const reach = CELL_SIZE * rangeMultiplier;
    return this.enemies.filter((e) => {
      if (e.hp <= 0) return false;
      const ex = e.gfx.x;
      const ey = e.gfx.y - 24;
      const dc = Math.abs(ex - px);
      const dr = Math.abs(ey - py);
      return dc <= reach && dr <= reach;
    });
  }

  private nearestEnemy(maxRange?: number): ActiveEnemy | null {
    let best: ActiveEnemy | null = null;
    let bestDist = maxRange ?? Infinity;
    const px = this.player.x;
    const py = this.player.y - 24;
    for (const e of this.enemies) {
      if (e.hp <= 0) continue;
      const ey = e.gfx.y - 24;
      const d = Phaser.Math.Distance.Between(px, py, e.gfx.x, ey);
      if (d < bestDist) {
        bestDist = d;
        best = e;
      }
    }
    return best;
  }

  private syncActiveTarget(): void {
    const inRange = this.nearestEnemy(PLAYER_ATTACK_RANGE);
    const focus = inRange ?? this.nearestEnemy();
    if (focus === this.activeTarget) {
      return;
    }
    this.activeTarget = focus;
    for (const e of this.enemies) {
      if (e.gfx instanceof Phaser.GameObjects.Rectangle) {
        e.gfx.setStrokeStyle(e === focus ? 3 : 2, e === focus ? 0xffee88 : 0xff8888);
      } else if (e.gfx instanceof Phaser.GameObjects.Sprite || e.gfx instanceof Phaser.GameObjects.Image) {
        e.gfx.setTint(e === focus ? 0xffeeaa : 0xffffff);
      }
    }
    this.emitFocusTargetChanged(0);
  }

  private tickPlayerAutoAttack(delta: number): void {
    if (!this.autoChaseEnabled) return;
    this.playerAttackCooldownMs = Math.max(0, this.playerAttackCooldownMs - delta);
    if (this.playerAttackCooldownMs > 0 || this.attackingAnim) return;

    const target = this.nearestEnemy(PLAYER_ATTACK_RANGE);
    if (!target) return;

    const gap = attackGapMs(this.playerCombatRatings().level, this.playerCombatRatings().attackSpeed);
    this.playerAttackCooldownMs = gap;
    this.activeTarget = target;
    this.resolveStrike(target);

    if (this.anims.exists("player-attack")) {
      this.attackingAnim = true;
      this.player.play("player-attack");
      this.player.once("animationcomplete", (anim: Phaser.Animations.Animation) => {
        if (anim.key !== "player-attack") return;
        this.attackingAnim = false;
        if (this.anims.exists("player-idle")) this.player.play("player-idle", true);
      });
    }
  }

  private refreshDepths(): void {
    this.player.setDepth(this.player.y);
    for (const e of this.enemies) {
      e.gfx.setDepth(e.gfx.y);
    }
  }

  private checkVictory(): void {
    if (this.ended) return;
    if (this.pendingSpawns.length > 0) return;
    if (this.enemies.some((e) => e.hp > 0)) return;
    this.onFloorCleared();
  }

  private onFloorCleared(): void {
    if (this.ended) return;
    this.ended = true;
    this.stopCombat();
    const baseXp = this.totalXpEarned > 0 ? this.totalXpEarned : this.plan.totalXpReward;
    window.dispatchEvent(
      new CustomEvent(TowerEvents.FloorCleared, {
        detail: { floor: this.floor, baseXp, enemiesKilled: this.enemiesKilled },
      }),
    );
  }

  private onReviveAccepted = (): void => {
    if (!this.playerDowned || this.ended) return;
    this.revivePlayer();
  };

  private onBattleGiveUp = (): void => {
    if (this.ended) return;
    if (this.playerDowned) {
      this.finalizeDefeat(false);
      return;
    }
    if (this.battleActive) {
      this.finalizeDefeat(true);
    }
  };

  private playPlayerDeathEffect(): void {
    this.player.anims?.stop();
    this.tweens.killTweensOf(this.player);
    this.tweens.add({
      targets: this.player,
      alpha: 0.45,
      angle: 92,
      y: this.player.y + 10,
      duration: 520,
      ease: "Cubic.easeOut",
    });
    this.player.setTint(0x553333);
    this.clearDeathFx();
    this.deathFx = this.add.graphics().setDepth(this.player.depth + 2);
    const g = this.deathFx;
    const cx = this.player.x;
    const cy = this.player.y - 28;
    g.fillStyle(0xff2222, 0.22);
    g.fillCircle(cx, cy, 40);
    g.lineStyle(2, 0xff8888, 0.7);
    g.strokeCircle(cx, cy, 34);
    this.tweens.add({
      targets: g,
      alpha: { from: 1, to: 0.35 },
      duration: 700,
      yoyo: true,
      repeat: -1,
    });
  }

  private clearDeathFx(): void {
    this.deathFx?.destroy();
    this.deathFx = null;
  }

  private onHealthPotionHeal = (event: Event): void => {
    if (!this.battleActive || this.playerDowned) return;
    const amount = (event as CustomEvent<{ amount: number }>).detail?.amount ?? 0;
    if (amount <= 0) return;
    const before = this.playerHp;
    this.playerHp = Math.min(this.playerMaxHp, this.playerHp + amount);
    if (this.playerHp > before) {
      this.emitPlayerHpChanged(0);
    }
  };

  private revivePlayer(): void {
    this.playerDowned = false;
    this.clearDeathFx();
    this.playerHp = this.playerMaxHp;
    this.player.setAlpha(1);
    this.player.setAngle(0);
    this.player.clearTint();
    if (this.anims.exists("player-idle")) {
      this.player.play("player-idle", true);
    }
    this.emitPlayerHpChanged(0);
    this.battleActive = true;
    this.tweens.add({
      targets: this.player,
      y: this.player.y - 14,
      duration: 280,
      ease: "Back.easeOut",
      yoyo: true,
    });
    if (this.pendingSpawns.length > 0) {
      const stub = this.pendingSpawns[0];
      this.scheduleNextSpawn(stub);
    }
  }

  private finalizeDefeat(surrendered = false): void {
    if (this.ended) return;
    this.playerDowned = false;
    this.clearDeathFx();
    this.ended = true;
    this.stopCombat();
    window.dispatchEvent(
      new CustomEvent(TowerEvents.BattleEnded, {
        detail: { floor: this.floor, victory: false, surrendered },
      }),
    );
  }

  private onPlayerDefeated(): void {
    if (this.ended || this.playerDowned) return;
    this.playerDowned = true;
    this.stopCombat();
    this.playPlayerDeathEffect();
    window.dispatchEvent(
      new CustomEvent(TowerEvents.PlayerDowned, {
        detail: { floor: this.floor },
      }),
    );
  }

  private removeEnemy(enemy: ActiveEnemy): void {
    enemy.gfx.destroy();
    this.enemies = this.enemies.filter((e) => e !== enemy);
    if (this.activeTarget === enemy) this.activeTarget = null;
    this.emitFocusTargetChanged(0);
  }

  private onEnemyDefeated(enemy: ActiveEnemy): void {
    this.enemiesKilled += 1;
    this.totalXpEarned += enemy.spawn.xpReward;
    window.dispatchEvent(new CustomEvent(TowerEvents.EnemyDefeated));
    this.removeEnemy(enemy);
    if (this.pendingSpawns.length === 0 && this.enemies.length === 0) {
      this.onFloorCleared();
    }
  }

  private resolvePetStrike(target: ActiveEnemy, damageMultiplier = 1): void {
    if (target.hp <= 0 || !this.equippedPetDef) return;
    const attacker = petCombatRatings(this.progression, this.equippedPetDef.statAllocation);
    const result = resolveHit(attacker, target.ratings, damageMultiplier);
    const ex = target.gfx.x;
    const ey = target.gfx.y - 20;
    if (this.combatFloaters) {
      if (result.kind === "miss") {
        this.combatFloaters.spawn(ex, ey, "miss");
      } else {
        this.combatFloaters.spawn(ex, ey, result.crit ? "crit" : "hit", result.damage);
      }
    }
    if (result.kind === "hit") {
      target.hp = Math.max(0, target.hp - result.damage);
      this.emitFocusTargetChanged(result.damage);
      if (target.hp <= 0) {
        target.gfx.setAlpha(0.35);
        this.onEnemyDefeated(target);
      }
    }
  }

  private resolveStrike(target: ActiveEnemy, damageMultiplier = 1): void {
    if (target.hp <= 0) return;
    const attacker = this.playerCombatRatings();
    const result = resolveHit(attacker, target.ratings, damageMultiplier);
    const ex = target.gfx.x;
    const ey = target.gfx.y - 20;
    if (this.combatFloaters) {
      if (result.kind === "miss") {
        this.combatFloaters.spawn(ex, ey, "miss");
      } else {
        this.combatFloaters.spawn(ex, ey, result.crit ? "crit" : "hit", result.damage);
      }
    }
    if (result.crit) {
      this.player.setTint(CRIT_TINT_GOLD);
      this.time.delayedCall(140, () => {
        if (this.player.active) this.player.clearTint();
      });
    }
    if (result.kind === "hit") {
      target.hp = Math.max(0, target.hp - result.damage);
      this.emitFocusTargetChanged(result.damage);
      this.tweens.add({
        targets: target.gfx,
        x: ex + 6,
        duration: 45,
        yoyo: true,
        repeat: 2,
      });
      if (target.hp <= 0) {
        target.gfx.setAlpha(0.35);
        this.onEnemyDefeated(target);
      }
    }
    this.facePlayerToward(target.gfx.x);
  }
}
